import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api/client';
import { PillButton } from '../components/ui/PillButton';
import { Chip } from '../components/ui/Chip';
import { useFocusTimer } from '../lib/focus/store';
import { focusController } from '../lib/focus/sw';
import {
  unlockAudio,
  playEndChime,
  playEndBlip,
  getNotificationPermission,
  requestNotificationPermission,
  notifySessionComplete,
} from '../lib/focus/sound';
import { formatRemaining, formatDate } from '../lib/format';
import { useDialog } from '../lib/dialog/hooks';
import type { FocusSession } from '@mindrift/shared';

export const Route = createFileRoute('/_app/focus')({
  component: FocusPage,
});

const FOCUS_DURATIONS = [15, 25, 50] as const;

function FocusPage() {
  const qc = useQueryClient();
  const dialog = useDialog();
  const session = useFocusTimer((s) => s.session);
  const status = useFocusTimer((s) => s.status);
  const remainingMs = useFocusTimer((s) => s.remainingMs);
  const setSessionStore = useFocusTimer((s) => s.setSession);
  const clearStore = useFocusTimer((s) => s.clear);
  const pendingTaskId = useFocusTimer((s) => s.pendingTaskId);
  const setPendingTask = useFocusTimer((s) => s.setPendingTask);
  const [selectedDuration, setSelectedDuration] = useState<number>(25);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [notifPromptShown, setNotifPromptShown] = useState(false);
  const chimedFor = useRef<string | null>(null);

  const { data: tasksData } = useQuery({
    queryKey: ['tasks', 'incomplete'],
    queryFn: () => api.tasks.list({ status: 'in_progress' }),
  });
  const tasks = tasksData?.tasks ?? [];

  const { data: historyData } = useQuery({
    queryKey: ['focus', 'history'],
    queryFn: () => api.focus.list(),
  });

  useEffect(() => {
    if (pendingTaskId && taskId === null) {
      setTaskId(pendingTaskId);
      setPendingTask(null);
    }
  }, [pendingTaskId, taskId, setPendingTask]);

  // Rehydrate: ping SW + ask API for an active session. Whichever returns
  // a valid running session first wins. Handles refresh / tab close / SW restart.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [pong, active] = await Promise.all([
        focusController.ping().catch(() => null),
        api.focus.active().catch(() => ({ session: null })),
      ]);
      if (cancelled) return;
      const swSession: FocusSession | null = pong?.sessionId
        ? ({
            id: pong.sessionId,
            userId: '',
            taskId: null,
            duration: Math.round((pong.durationMs ?? 0) / 60_000),
            actualDuration: null,
            status: 'running',
            startedAt: new Date(pong.startedAt ?? Date.now()).toISOString(),
            endedAt: null,
          } as FocusSession)
        : null;
      const candidate: FocusSession | null = active.session ?? swSession;
      if (!candidate || candidate.status !== 'running') return;
      const startedAt = new Date(candidate.startedAt).getTime();
      const durationMs = candidate.duration * 60_000;
      const elapsed = Date.now() - startedAt;
      if (elapsed >= durationMs) {
        // Session already completed while page was closed
        try {
          await api.focus.end(candidate.id, { status: 'completed' });
          useFocusTimer.getState().rehydrate(candidate, startedAt, durationMs);
          qc.invalidateQueries({ queryKey: ['focus'] });
        } catch {
          // ignore — will retry on next mount
        }
        return;
      }
      useFocusTimer.getState().rehydrate(candidate, startedAt, durationMs);
      // Ensure SW is in sync
      if (!swSession || swSession.id !== candidate.id) {
        await focusController.start(candidate.id, candidate.duration, { startedAt, durationMs });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qc]);

  useEffect(() => {
    const unsub = focusController.subscribe((msg) => {
      if (msg.type === 'TICK') {
        useFocusTimer.getState().tick(Date.now());
      } else if (msg.type === 'COMPLETE') {
        const s = useFocusTimer.getState().session;
        if (s && useFocusTimer.getState().status === 'running') {
          endSession.mutate({ id: s.id, status: 'completed' });
        }
      }
    });
    return unsub;
    // endSession is a stable React Query mutation object — safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let id: number | null = null;
    if (status === 'running') {
      id = window.setInterval(() => {
        useFocusTimer.getState().tick(Date.now());
      }, 250);
    }
    return () => {
      if (id) clearInterval(id);
    };
  }, [status]);

  // Fire chime + system notification when session naturally reaches 00:00
  useEffect(() => {
    if (status !== 'ended' || !session) return;
    if (chimedFor.current === session.id) return;
    chimedFor.current = session.id;
    playEndChime();
    notifySessionComplete(session.duration);
  }, [status, session]);

  const start = useMutation({
    mutationFn: () => api.focus.start({ duration: selectedDuration, taskId }),
    onSuccess: async (res) => {
      unlockAudio();
      setSessionStore(res.session, res.session.duration * 60_000);
      await focusController.start(res.session.id, res.session.duration);
      qc.invalidateQueries({ queryKey: ['focus'] });
    },
  });

  const endSession = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'completed' | 'cancelled' }) =>
      api.focus.end(id, { status }),
    onSuccess: () => {
      focusController.cancel();
      clearStore();
      qc.invalidateQueries({ queryKey: ['focus'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const markTaskAndClose = useMutation({
    mutationFn: async (taskId: string) => {
      await api.tasks.update(taskId, { status: 'completed' });
      if (session) {
        await api.focus.end(session.id, { status: 'completed' });
      }
    },
    onSuccess: () => {
      focusController.cancel();
      clearStore();
      qc.invalidateQueries({ queryKey: ['focus'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const removeSession = useMutation({
    mutationFn: (id: string) => api.focus.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['focus'] }),
  });

  const skipTaskAndClose = useMutation({
    mutationFn: async () => {
      if (session) {
        await api.focus.end(session.id, { status: 'completed' });
      }
    },
    onSuccess: () => {
      focusController.cancel();
      clearStore();
      qc.invalidateQueries({ queryKey: ['focus'] });
    },
  });

  const handleEndNow = async () => {
    if (!session) return;
    const ok = await dialog.confirm(
      'End the focus session now?',
      'Your progress will be saved and the timer will stop.',
      { confirmLabel: 'End now' },
    );
    if (ok) {
      playEndBlip();
      endSession.mutate({ id: session.id, status: 'completed' });
    }
  };

  const handleStart = async () => {
    if (!notifPromptShown && getNotificationPermission() === 'default') {
      setNotifPromptShown(true);
      const perm = await requestNotificationPermission();
      if (perm === 'denied') {
        await dialog.alert(
          'Notifications blocked',
          'You can still hear the chime in this tab, but system notifications are off. Enable them in your browser settings if you change your mind.',
        );
      }
    }
    start.mutate();
  };

  const running = status === 'running';
  const ended = status === 'ended';
  const linkedTask = session?.taskId ? tasks.find((t) => t.id === session.taskId) : null;

  return (
    <div className="bg-deep-enterprise-green text-canvas-white min-h-[calc(100vh-4rem)]">
      <div className="container-editorial py-12 sm:py-16 md:py-20 lg:py-section">
        <p className="mono-label text-canvas-white/60">FOCUS</p>
        <h1 className="font-display text-section-display text-canvas-white mt-3">
          {running ? 'Deep work in progress.' : ended ? 'Session complete.' : 'Time to focus.'}
        </h1>

        <div className="mt-12 max-w-2xl">
          {!running && !ended && (
            <>
              <div className="flex flex-wrap gap-3 mb-8">
                {FOCUS_DURATIONS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setSelectedDuration(d)}
                    className={`pill-outline border-canvas-white/40 text-canvas-white ${
                      selectedDuration === d ? 'bg-canvas-white text-deep-enterprise-green border-canvas-white' : ''
                    }`}
                    type="button"
                  >
                    {d} min
                  </button>
                ))}
                <button
                  onClick={async () => {
                    const input = await dialog.prompt(
                      'Custom duration',
                      'Set a focus length in minutes (1–480).',
                      '30',
                      { inputLabel: 'Minutes', inputType: 'number' },
                    );
                    if (input == null) return;
                    const n = parseInt(String(input), 10);
                    if (Number.isFinite(n) && n > 0 && n <= 480) setSelectedDuration(n);
                  }}
                  className="pill-outline border-canvas-white/40 text-canvas-white"
                  type="button"
                >
                  Custom
                </button>
              </div>

              {tasks.length > 0 && (
                <div className="mb-8">
                  <p className="mono-label text-canvas-white/60">LINK A TASK (OPTIONAL)</p>
                  <select
                    value={taskId ?? ''}
                    onChange={(e) => setTaskId(e.target.value || null)}
                    style={{ color: '#17171c', backgroundColor: '#ffffff' }}
                    className="mt-3 w-full rounded-md border border-canvas-white/30 px-3 py-3 text-body focus:outline-none focus:ring-2 focus:ring-canvas-white/40"
                  >
                    <option value="" style={{ color: '#17171c', backgroundColor: '#ffffff' }}>— No task —</option>
                    {tasks.map((t) => (
                      <option key={t.id} value={t.id} style={{ color: '#17171c', backgroundColor: '#ffffff' }}>
                        {t.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <PillButton
                variant="primary-inverse"
                onClick={handleStart}
                loading={start.isPending}
              >
                Start {selectedDuration}-minute session
              </PillButton>
            </>
          )}

          {running && (
            <>
              <div
                className="font-display leading-none tracking-tight tabular-nums text-canvas-white"
                style={{ fontSize: 'clamp(64px, 18vw, 180px)' }}
                aria-live="polite"
              >
                {formatRemaining(remainingMs)}
              </div>
              <p className="mt-4 text-body text-canvas-white/70">
                {session?.taskId
                  ? `Linked to: ${linkedTask?.title ?? 'task'}`
                  : 'No task linked'}
              </p>
              <div className="mt-8 flex gap-3">
                <PillButton
                  variant="primary-inverse"
                  onClick={handleEndNow}
                  disabled={endSession.isPending}
                >
                  End now
                </PillButton>
                <PillButton
                  variant="outline"
                  className="!border-canvas-white/40 !text-canvas-white"
                  onClick={async () => {
                    const ok = await dialog.confirm(
                      'Cancel this session?',
                      'This will mark the session as cancelled. Your history will still be saved.',
                      { tone: 'danger', confirmLabel: 'Cancel session' },
                    );
                    if (ok) endSession.mutate({ id: session!.id, status: 'cancelled' });
                  }}
                >
                  Cancel
                </PillButton>
              </div>
            </>
          )}

          {ended && session && (
            <>
              <div className="font-display text-card-heading text-canvas-white">Nice work.</div>
              <p className="mt-3 text-body-lg text-canvas-white/70 max-w-md">
                You focused for {session.duration} minutes{session.actualDuration != null && session.actualDuration < session.duration ? ` (ended early at ${session.actualDuration} min)` : ''}. Take a breath.
              </p>

              {linkedTask && (
                <div className="mt-8 rounded-md border border-canvas-white/20 bg-canvas-white/5 p-5">
                  <p className="mono-label text-canvas-white/60">LINKED TASK</p>
                  <p className="mt-2 text-body-lg text-canvas-white">{linkedTask.title}</p>
                  <p className="mt-3 text-body text-canvas-white/80">
                    Did you complete this task?
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <PillButton
                      variant="primary-inverse"
                      onClick={() => markTaskAndClose.mutate(linkedTask.id)}
                      loading={markTaskAndClose.isPending}
                    >
                      Yes, mark complete
                    </PillButton>
                    <PillButton
                      variant="outline"
                      className="!border-canvas-white/40 !text-canvas-white"
                      onClick={() => skipTaskAndClose.mutate()}
                      loading={skipTaskAndClose.isPending}
                    >
                      No, keep it open
                    </PillButton>
                  </div>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <PillButton
                  variant="outline"
                  className="!border-canvas-white/40 !text-canvas-white"
                  onClick={() => {
                    if (!linkedTask) endSession.mutate({ id: session.id, status: 'completed' });
                    else skipTaskAndClose.mutate();
                  }}
                >
                  {linkedTask ? 'Close without changes' : 'Close session'}
                </PillButton>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-canvas-white text-ink">
        <div className="container-editorial py-12 sm:py-16 md:py-20 lg:py-section">
          <p className="mono-label text-muted-slate">HISTORY</p>
          <h2 className="font-display text-card-heading text-ink mt-2">Recent focus sessions</h2>
          {historyData && historyData.sessions.length > 0 ? (
            <ul className="mt-6 border-t border-hairline">
              {historyData.sessions.slice(0, 20).map((s) => (
                <li key={s.id} className="border-b border-hairline py-3 flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-4 text-body">
                  <span className="text-caption text-muted-slate w-28 sm:w-32 shrink-0">{formatDate(s.startedAt)}</span>
                  <span className="w-16 sm:w-20 shrink-0">{s.actualDuration ?? s.duration} min</span>
                  <Chip tone={s.status === 'completed' ? 'green' : s.status === 'cancelled' ? 'muted' : 'warning'}>
                    {s.status}
                  </Chip>
                  <span className="text-caption text-muted-slate sm:ml-auto basis-full sm:basis-auto truncate">
                    {tasks.find((t) => t.id === s.taskId)?.title ?? '—'}
                  </span>
                  <button
                    onClick={async () => {
                      const ok = await dialog.confirm(
                        'Delete this session?',
                        'This will remove it from your history.',
                        { tone: 'danger', confirmLabel: 'Delete' },
                      );
                      if (ok) removeSession.mutate(s.id);
                    }}
                    className="text-caption text-muted-slate hover:text-error-red shrink-0 px-2 py-1"
                    aria-label="Delete session"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-6 text-muted-slate">No focus sessions yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
